// Chat di project ini, the project's conversations (design review
// ui.region_gate project_page section_container: rows). One link row per
// conversation, newest first: the chat glyph, the title (one line, ellipsis)
// and when it last moved. The open conversation is the current row. The
// global Riwayat never lists these (GET /chat/conversations leaves project
// conversations out unless asked for one project).
import type { ReactNode } from "react";
import * as API from "../api";
import type { ConversationSummary } from "../api";
import { formatRelative } from "../lib/format";
import { useChatShell } from "../shell/chat-context";
import { Notice, useAsync, useDelayed } from "../ui/components";
import { Glyph } from "../ui/icons";

/** The path of one conversation inside a project. */
export function projectConversationPath(projectId: string, conversationId: string): string {
  return `/project/${encodeURIComponent(projectId)}/c/${encodeURIComponent(conversationId)}`;
}

/** The project's conversations, reloaded after every chat turn. */
export function useProjectConversations(projectId: string) {
  const tick = useChatShell()?.historyTick ?? 0;
  return useAsync<ConversationSummary[]>(() => API.listProjectConversations(projectId), [projectId, tick]);
}

export interface ProjectChatsProps {
  projectId: string;
  /** The open conversation, or null. */
  currentId?: string | null;
  headingId?: string;
}

export function ProjectChats({ projectId, currentId = null, headingId = "pj-chats-title" }: ProjectChatsProps) {
  const list = useProjectConversations(projectId);
  const shownSkeleton = useDelayed(list.loading && !list.data);

  let body: ReactNode;
  if (list.loading && !list.data) {
    body = (
      <div className="pj-rows-sk" role="status" aria-busy="true" aria-label="Memuat chat project">
        {shownSkeleton ? Array.from({ length: 3 }, (_, i) => <span key={i} className="sk pj-row-sk" aria-hidden="true" />) : null}
      </div>
    );
  } else if (list.error) {
    body = (
      <Notice
        tone="danger"
        title="Chat project belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={list.reload}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(list.error)}</p>
      </Notice>
    );
  } else {
    const rows = list.data ?? [];
    body =
      rows.length === 0 ? (
        <p className="pj-empty">Belum ada chat di project ini. Pertanyaan pertama Anda di sini akan tersimpan di project ini.</p>
      ) : (
        <ul className="pj-rows" aria-labelledby={headingId}>
          {rows.map((c) => (
            <li key={c.id} className="pj-chat-item">
              <a className="pj-row pj-chat" href={projectConversationPath(projectId, c.id)} aria-current={c.id === currentId ? "page" : undefined}>
                <span className="pj-row-icon" aria-hidden="true">
                  <Glyph name="messages" size={20} />
                </span>
                <span className="pj-row-name" title={c.title}>
                  {c.title}
                </span>
                <span className="pj-row-time num">{formatRelative(c.lastMessageAt)}</span>
              </a>
            </li>
          ))}
        </ul>
      );
  }

  return (
    <section className="pj-panel pj-chats" aria-labelledby={headingId}>
      <div className="pj-panel-head">
        <h2 id={headingId} className="pj-panel-title">
          Chat di project ini
        </h2>
      </div>
      {body}
    </section>
  );
}
